import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Flex,
  Image,
  Center,
  Link,
  Checkbox,
  InputGroup,
  InputRightElement,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import bgImage from '../assets/images/HansCard_L.png';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LogoWithBackground from '../components/LogoWithBackground';

export default function Login() {
  // Add translation hook
  const { t } = useTranslation();

  // State hooks
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Auth and navigation hooks
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Theme values
  const formBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.85)',
    `rgba(34, 35, 57, 0.85)`
  );
  const borderColor = useColorModeValue('gray.200', 'rgba(255, 255, 255, 0.1)');
  const shadowColor = useColorModeValue(
    '0 4px 20px rgba(0, 0, 0, 0.1)',
    '0 4px 20px rgba(0, 0, 0, 0.3)'
  );
  const backdropBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.4)',
    'rgba(0, 0, 0, 0.4)'
  );
  const logoFilter = useColorModeValue('none', 'brightness(1.2)');
  const inputBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.5)');
  const inputFocusBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.8)');
  const hoverBoxShadow = useColorModeValue(
    '0 8px 30px rgba(0, 0, 0, 0.12)',
    '0 8px 30px rgba(0, 0, 0, 0.4)'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isSignUp) {
        await signUp(email, password);
        toast({
          title: t('auth.signUpSuccess'),
          description: t('auth.resetLinkSent'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await signIn(email, password, rememberMe);
        navigate('/dashboard');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('auth.authError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      width="100vw"
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      backgroundImage={`url(${bgImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      overflow="hidden"
    >
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        alignItems="center"
        justifyContent="center"
        backdropFilter="blur(5px)"
        bg={backdropBgColor}
      >
        <Container
          maxW="md"
          py={10}
          px={4}
          position="relative"
          zIndex={1}
        >
          <Center mb={8}>
            <LogoWithBackground
              width="200px"
              objectFit="contain"
              transition="filter 0.2s ease"
            />
          </Center>

          <Box
            p={8}
            width="100%"
            bg={formBgColor}
            boxShadow={shadowColor}
            borderRadius="xl"
            border="1px"
            borderColor={borderColor}
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{
              boxShadow: hoverBoxShadow,
              transform: 'translateY(-2px)'
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t('auth.email')}</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.email')}
                    bg={inputBgColor}
                    _focus={{
                      bg: inputFocusBgColor,
                      borderColor: 'brand.500'
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t('auth.password')}</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.password')}
                    bg={inputBgColor}
                    _focus={{
                      bg: inputFocusBgColor,
                      borderColor: 'brand.500'
                    }}
                  />
                </FormControl>

                {!isSignUp && (
                  <FormControl display="flex" alignItems="center">
                    <Checkbox
                      id="remember-me"
                      colorScheme="brand"
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <FormLabel htmlFor="remember-me" mb={0} ml={2} fontSize="sm">
                      {t('auth.rememberMe')}
                    </FormLabel>
                  </FormControl>
                )}

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                >
                  {isSignUp ? t('auth.signUp') : t('auth.signIn')}
                </Button>
              </Stack>
            </form>

            <VStack mt={6} spacing={4}>
              <HStack>
                <Text>
                  {isSignUp ? t('auth.haveAccount') : t('auth.noAccount')}
                </Text>
                <Button
                  variant="link"
                  colorScheme="brand"
                  onClick={() => setIsSignUp(!isSignUp)}
                >
                  {isSignUp ? t('auth.signIn') : t('auth.signUp')}
                </Button>
              </HStack>

              <Divider />

              <Text textAlign="right" mt={2} position="relative" zIndex={1}>
                <Link as={RouterLink} to="/forgot-password" color="blue.500" display="inline-block">
                  {t('auth.forgotPassword')}
                </Link>
              </Text>
            </VStack>
          </Box>
        </Container>
      </Flex>
    </Box>
  );
}
